import { SEARCH_REQUEST, SEARCH_SUCCESS, SEARCH_FAILURE } from '../store/cardOperationActions';
import { logCustomerCardPageAction } from './newRelicLogging';
import { maskSearchTerm } from './maskSearchTerm';
import type { CustomerCardState } from '../types';
import type { SearchResult } from '../types/search.types';

export const SEARCH_ATTEMPT_ACTION_TYPES = [SEARCH_REQUEST, SEARCH_SUCCESS, SEARCH_FAILURE] as const;

type SearchAttemptAction = { type: string; payload?: unknown };

/**
 * Logs each search attempt within an operation — how many times the dealer has had to search, and how
 * many results came back — to find out whether the lack of pagination (search only ever returns page
 * one, however many results that is) is actually costing users. Never logs the raw term: it's a
 * customer's name, so only a masked version goes to NR (see maskSearchTerm).
 *
 * `customerCard` must be the slice as it stood *after* the action's reducer ran — unlike
 * operationLifecycleLogger, this needs SEARCH_REQUEST's own attemptCount increment to have already
 * happened.
 */
export const logSearchAttemptEvent = (action: SearchAttemptAction, customerCard: CustomerCardState | undefined): void => {
    if (!customerCard?.operation) return;

    const { kind: operationKind, operationId } = customerCard.operation;
    const { dealId, dealerId } = customerCard;
    const attemptNumber = customerCard.search.attemptCount;

    switch (action.type) {
        case SEARCH_REQUEST:
            logCustomerCardPageAction('CustomerCardOperation', {
                event: 'search',
                operationKind,
                operationId,
                attemptNumber,
                maskedTerm: maskSearchTerm(action.payload as string),
                dealXgId: dealId,
                dealerId
            });
            return;
        case SEARCH_SUCCESS:
            logCustomerCardPageAction('CustomerCardOperation', {
                event: 'searchResult',
                status: 'success',
                operationKind,
                operationId,
                attemptNumber,
                resultCount: (action.payload as SearchResult[]).length,
                dealXgId: dealId,
                dealerId
            });
            return;
        case SEARCH_FAILURE:
            logCustomerCardPageAction('CustomerCardOperation', {
                event: 'searchResult',
                status: 'failure',
                operationKind,
                operationId,
                attemptNumber,
                dealXgId: dealId,
                dealerId
            });
            return;
    }
};
